import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { IoCloudUpload, IoClose } from 'react-icons/io5';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import './ImageUpload.css';

export default function ImageUpload({ onImageUploaded, currentImage, storagePath }) {
  const [preview, setPreview] = useState(currentImage || null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;

          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve({ blob, previewUrl: URL.createObjectURL(blob) });
          }, 'image/jpeg', 0.75);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Seules les images sont acceptées');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    setError('');
    setUploading(true);

    try {
      const { blob, previewUrl } = await compressImage(file);
      setPreview(previewUrl);

      const path = storagePath || `covers/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);

      onImageUploaded(url);
    } catch (err) {
      console.error('Erreur upload couverture:', err);
      setError("Erreur lors de l'upload. Réessaie.");
      setPreview(null);
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleRemove = () => {
    setPreview(null);
    onImageUploaded(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="img-upload">
      {preview ? (
        <div className="img-upload-preview">
          <img src={preview} alt="Couverture" className="img-upload-img" />
          {uploading && (
            <div className="img-upload-loading">
              <div className="img-upload-spinner" />
              <span>Compression et upload...</span>
            </div>
          )}
          {!uploading && (
            <motion.button
              className="img-upload-remove"
              onClick={handleRemove}
              whileTap={{ scale: 0.9 }}
              type="button"
            >
              <IoClose />
            </motion.button>
          )}
        </div>
      ) : (
        <motion.label
          className="img-upload-zone"
          whileHover={{ borderColor: 'var(--amber)' }}
          whileTap={{ scale: 0.98 }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <IoCloudUpload className="img-upload-icon" />
          <span className="img-upload-text">Ajouter une couverture</span>
          <span className="img-upload-hint">JPG, PNG · Max 10 Mo · Compressée automatiquement</span>
        </motion.label>
      )}
      {error && <p className="img-upload-error">{error}</p>}
    </div>
  );
}